import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Notifications from '../Notifications/Notifications';
import {
  IconChevronDown,
  IconLogout,
  IconMenu,
  IconMoon,
  IconShield,
  IconSun,
  IconUser,
} from '../icons/Icons';
import './Header.css';

interface HeaderProps {
  /** Открыть боковое меню на мобильных. */
  onOpenMenu?: () => void;
}

/** Заголовок раздела в верхней панели по текущему маршруту. */
function getPageTitle(pathname: string): string {
  if (pathname.startsWith('/dashboard')) return 'Дашборд';
  if (pathname.startsWith('/area')) return 'Участок';
  if (pathname.startsWith('/plates')) return 'Гос. номера';
  if (pathname.startsWith('/balance')) return 'Баланс';
  if (pathname.startsWith('/profile')) return 'Профиль';
  if (pathname.startsWith('/subaccounts')) return 'Субаккаунты';
  if (pathname.startsWith('/admin')) return 'Админ-панель';
  return 'Flypix';
}

const Header: React.FC<HeaderProps> = ({ onOpenMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { resolvedTheme, toggleTheme } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserMenuOpen]);

  const closeUserMenu = () => setIsUserMenuOpen(false);

  const handleProfileClick = () => {
    closeUserMenu();
    navigate('/profile');
  };

  const handleAdminClick = () => {
    closeUserMenu();
    navigate('/admin');
  };

  const handleLogout = async () => {
    closeUserMenu();
    await logout();
    navigate('/login');
  };

  return (
    <header className="topbar">
      <div className="topbar__left">
        <button
          type="button"
          className="topbar__burger"
          aria-label="Открыть меню"
          onClick={onOpenMenu}
        >
          <IconMenu size={20} />
        </button>

        <h2 className="topbar__title">{getPageTitle(location.pathname)}</h2>
      </div>

      <div className="topbar__right">
        <button
          type="button"
          className="topbar__icon-btn topbar__theme"
          onClick={toggleTheme}
          aria-label={
            resolvedTheme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'
          }
          title={resolvedTheme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
        >
          {resolvedTheme === 'dark' ? <IconSun size={19} /> : <IconMoon size={19} />}
        </button>

        <Notifications />

        {user && !user.is_subaccount && (
          <button
            type="button"
            className="topbar__balance"
            aria-label="Перейти на страницу баланса"
            onClick={() => navigate('/balance')}
          >
            <span className="topbar__balance-label">Баланс</span>
            <span className="topbar__balance-value">
              {(user.balance ?? 0).toLocaleString('ru-RU')} ₽
            </span>
          </button>
        )}

        <div className="topbar__user" ref={userMenuRef}>
          <button
            type="button"
            className="topbar__user-trigger"
            aria-label="Меню пользователя"
            aria-expanded={isUserMenuOpen}
            aria-haspopup="menu"
            onClick={() => setIsUserMenuOpen((prev) => !prev)}
          >
            <span className="topbar__avatar" aria-hidden="true">
              {(user?.name || 'F').trim().charAt(0).toUpperCase()}
            </span>
            <span className="topbar__user-name">{user?.name || 'Пользователь'}</span>
            <IconChevronDown
              size={16}
              className={`topbar__chevron${
                isUserMenuOpen ? ' topbar__chevron--open' : ''
              }`}
            />
          </button>

          {isUserMenuOpen && (
            <div className="ui-menu topbar__menu" role="menu">
              <button
                type="button"
                className="ui-menu__item"
                role="menuitem"
                onClick={handleProfileClick}
              >
                <IconUser size={16} />
                Профиль
              </button>
              {isAdmin && (
                <button
                  type="button"
                  className="ui-menu__item"
                  role="menuitem"
                  onClick={handleAdminClick}
                >
                  <IconShield size={16} />
                  Админ-панель
                </button>
              )}
              <div className="ui-menu__sep" role="separator" />
              <button
                type="button"
                className="ui-menu__item ui-menu__item--danger"
                role="menuitem"
                onClick={() => void handleLogout()}
              >
                <IconLogout size={16} />
                Выйти
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
